import React, { useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { trackFieldEdit, valuesAreEqual } from '../../../services/review-field-edit-tracking-service';
import { ReviewEditTrackingContext } from './ReviewEditTrackingContext';

const findOptionLabel = (options, value) => {
  if (!Array.isArray(options) || value === null || value === undefined) return undefined;
  const match = options.find(option => String(option.value) === String(value));
  return match ? match.display_name : undefined;
};

// Objects/arrays can be mutated in place elsewhere (e.g. the voucher contact form edits the
// Redux contact object directly), so baselines are stored as snapshots.
const snapshot = value => (value !== null && typeof value === 'object' ? JSON.parse(JSON.stringify(value)) : value);

const ReviewEditTrackingProvider = ({ children }) => {
  const dealId = useSelector(state => state.deal.deal?.id);
  const dealType = useSelector(state => state.deal.deal?.deal_type);
  const voucherId = useSelector(state => state.voucher.voucher?.id);
  const isEarlyInvoice = useSelector(state => !!state.voucher.voucher?.is_early_invoice);
  const actingAsUserId = useSelector(state => state.proxy?.proxySettings?.id);

  // Read through a ref so `trackEdit` (and therefore the context value) never changes identity
  // and consumers never re-render because deal/voucher data changed.
  const metaRef = useRef({});
  metaRef.current = {
    dealId, dealType, voucherId, isEarlyInvoice, actingAsUserId,
  };
  // `previous_value` is the ORIGINAL value when Review loaded: the first value seen for a
  // given field/entity before its first edit in this Review session.
  const baselinesRef = useRef(new Map());
  // Last value reported per field/entity, so a change + blur double commit (or a re-render
  // race before the saved value flows back) never reports the same value twice.
  const lastReportedRef = useRef(new Map());

  const trackEdit = useCallback(({
    fieldKey, fieldLabel, section, seedPrevious, currentValue, options, bounds,
    entityType, entityId, spaceIndex, partyType, rowType,
  }) => {
    try {
      if (valuesAreEqual(seedPrevious, currentValue)) return;
      const key = [fieldKey, entityType, entityId, spaceIndex].map(part => part ?? '').join('|');
      if (!baselinesRef.current.has(key)) baselinesRef.current.set(key, snapshot(seedPrevious));
      if (lastReportedRef.current.has(key) && valuesAreEqual(lastReportedRef.current.get(key), currentValue)) return;
      lastReportedRef.current.set(key, snapshot(currentValue));
      const previousValue = baselinesRef.current.get(key);

      trackFieldEdit({
        ...metaRef.current,
        fieldKey,
        fieldLabel,
        section,
        previousValue,
        currentValue,
        previousLabel: findOptionLabel(options, previousValue),
        currentLabel: findOptionLabel(options, currentValue),
        bounds,
        entityType,
        entityId,
        spaceIndex,
        partyType,
        rowType,
      });
    } catch (error) {
      console.error('Failed to track broker verification field edit');
    }
  }, []);

  const value = useMemo(() => ({ trackEdit }), [trackEdit]);

  return (
    <ReviewEditTrackingContext.Provider value={value}>
      {children}
    </ReviewEditTrackingContext.Provider>
  );
};

ReviewEditTrackingProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ReviewEditTrackingProvider;
